import { type CSSProperties, type ReactNode, useEffect, useId, useRef, useState } from 'react';

import { Icon, type IconName } from '@/components/ui/Icon';
import { cn } from '@/utils/cn';

import styles from './Menu.module.css';

export interface MenuItem {
  label: string;
  icon?: IconName;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
}

export interface MenuProps {
  /** Visible content of the trigger button. */
  trigger: ReactNode;
  /** Accessible name of the trigger, when its content is only an icon. */
  label?: string;
  items: readonly MenuItem[];
  triggerClassName?: string | undefined;
  align?: 'start' | 'end';
}

const GAP_PX = 6;

/**
 * A dropdown of actions under its trigger. It is positioned against the viewport, so a table's
 * scroll container never clips it. Esc, a click outside, scrolling or resizing closes it.
 */
export function Menu({ trigger, label, items, triggerClassName, align = 'end' }: MenuProps) {
  const [position, setPosition] = useState<CSSProperties | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const open = position !== null;

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();

    const close = () => {
      setPosition(null);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  const toggle = () => {
    if (open) {
      setPosition(null);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition(
      align === 'end'
        ? { top: rect.bottom + GAP_PX, right: window.innerWidth - rect.right }
        : { top: rect.bottom + GAP_PX, left: rect.left },
    );
  };

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        ref={triggerRef}
        type="button"
        className={cn(styles.trigger, triggerClassName)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        onClick={toggle}
      >
        {trigger}
      </button>
      {position && (
        <div ref={listRef} id={menuId} role="menu" className={styles.menu} style={position}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className={cn(styles.item, item.tone === 'danger' && styles.danger)}
              onClick={() => {
                setPosition(null);
                item.onSelect();
              }}
            >
              {item.icon && <Icon name={item.icon} size={18} />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
